/*
 * @Author: wuqinfa
 * @Date: 2022-11-22 10:54:16
 * @LastEditors: wuqinfa
 * @Description: 适配 mxc 的接口，返回一个封装后的 axios 实例
 *    返回的 axios 实例包括以下内容：
 *      1. baseURL: CORE_API,
 *      2. headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
 *      3. timeout: 30000,
 *      4. withCredentials: true,
 *      5. 添加响应拦截器，用于：
 *          - 拆解接口返回的数据包
 *          - 把请求的报错信息格式化打印处理
 *          - 将部分错误情况的提示以 Dialog 的形式展示出来（可根据参数调整是否展示，以及自定义展示的方式）
 *            - 默认的错误情况提示请参考：src/utils/request/showErrorMessage.ts
 *    PS：
 *      - 上述包含的内容基本是目前我们 MXC 所有接口抽象归纳出来的最通用的内容。如果“某些情况”不是属于非常通用的话，不建议往 mxcFactory 中添加
 *      - CORE项目（CRM2）、 微服务项目（CRM3）或者是以后的新接口，有可能有很多其它特殊情况，建议基于 mxcFactory 返回的实例独立拓展，避免互相干扰
 */
import axios from 'axios';
import lodash from 'lodash';
// import { history } from 'umi';

import type { ErrorInfo } from './interface';
import printErrorInfo from './printErrorInfo';
import showErrorMessage from './showErrorMessage';

import * as cache from '@/utils/cache';
import { goToLogin, fileToJson, } from '@/utils';

/**
 * 适配 mxc 的接口，返回一个封装后的 axios 实例
 * @param {boolean | Function} mxcMessage true：将部分错误情况的提示以 Dialog 的形式展示出来，false：不展示；如果传了一个 Function 进来，则接口返回的数据传到该 Function 中
 */
export default function mxcFactory(mxcMessage: boolean | Function = true) {
  const headers: any = {
    // 项目大多数都是旧接口，大多数以 'Content-Type': 'application/x-www-form-urlencoded' 的方式传参数
    // 对于一些新接口，需要改成 json 格式作为参数的话，可以改成  'Content-Type': 'application/json'
    // 另外，如果需要传文件的，可以改成  'Content-Type': 'multipart/form-data'
    // 细节可以参考： [x-www-form-urlencoded与multipart/form-data的区别](https://blog.csdn.net/m0_37605642/article/details/90762739)
    'Content-Type': 'application/json',
  };
  const token = cache.get('school-token') || '';
  // console.log('token 3333 :>> ', token);

  if (token) {
    headers['school-token'] = token;
  }

  // 创建 axios 实例
  const instance = axios.create({
    baseURL: API_URL,
    headers: headers,
    timeout: 30000, // 设置 30s 的请求超时时间
    withCredentials: true,
  });

  // 给 axios 实例添加响应拦截器
  instance.interceptors.response.use(
    async (response) => {
      // http 状态码在 2xx 范围内的状态码都会触发该函数
      const { data, config, } = response;
      const {
        code,
        success = false,
      } = data || {};
      let messageData = data;
  
      if (success) {
        return data;
      }

      if (config.responseType === 'blob') {
        try {
          messageData = await fileToJson(data);
        } catch (error) {
          // 如果不能将 blob 转成 json，说明请求成功，正确返回文件内容
          return response;
        }
      }

      if (mxcMessage) {
        if (lodash.isFunction(mxcMessage)) {
          mxcMessage(messageData);
        } else {
          showErrorMessage(messageData);
        }
      }

      printErrorInfo(response as ErrorInfo);
      
      if (code === '40100') {
        goToLogin();
        return {}; // 返回一个对象的原因是外层有很多都是 res.data，不返回对象的话，会报错
      }
  
      return Promise.reject(data); // FIXME: 一旦走到这一步，程序会直接奔溃
    },
    error => {
      // http 状态码不在 2xx 范围内的状态码都会触发该函数
      printErrorInfo(error);
      return Promise.reject(error);
    },
  );

  return instance;
}